import {
  Box,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { ComponentType } from "react";
import {
  BillingRuleCondition,
  BillingRuleConditionOperator,
  BillingRuleConditionParameter,
  BillingRuleConditionParameterType,
  BillingRuleConditionTarget,
  BusinessEntityType,
  GetCustomerDetailsQuery,
  SupplierContractCondition,
  SupplierContractConditionTarget,
} from "../../../graphql/generated";
import billingRuleConditionOperatorLabel from "../../../utils/labels/billingRule/billingRuleConditionOperatorLabel";
import billingRuleConditionTargetLabel from "../../../utils/labels/billingRule/billingRuleConditionTargetLabel";
import BusinessEntityInput from "./value-selectors/BusinessEntityInput";
import DateInput from "./value-selectors/DateInput";
import DurationInput from "./value-selectors/DurationInput";
import GoodsInput from "./value-selectors/GoodsInput";
import MileageInput from "./value-selectors/MileageInput";
import TimeInput from "./value-selectors/TimeInput";
import TractorInput from "./value-selectors/TractorInput";
import TrailerInput from "./value-selectors/TrailerInput";
import TrailerTypeSelect from "./value-selectors/TrailerTypeSelect";
import { useTranslation } from "react-i18next";
import enumLabel from "../../../utils/labels/enumLabel";
import NumberInput from "./value-selectors/NumberInput";
import VolumeInput from "./value-selectors/VolumeInput";
import ConditionParameterForm from "./ConditionParameterForm";
import ConditionStateSelect from "./value-selectors/ConditionStateSelect";
import CityInput from "./value-selectors/CityInput";
import { testIds } from "./ConditionForm.testIds";
import {
  buildConditionParameters,
  conditionParametersPerTarget,
  visibleConditionParameters,
} from "../conditionParameters";

export type ConditionValueSelectorComponentProps = {
  value: number | string | string[] | null;
  onChange: (value: number | string | string[] | null) => void;
  customer?: GetCustomerDetailsQuery["customerById"];
  label?: string | null;
};

export type ConditionValueSelectorComponent =
  ComponentType<ConditionValueSelectorComponentProps>;

const ConditionValueSelectorComponentByTarget: {
  [key in
    | BillingRuleConditionTarget
    | SupplierContractConditionTarget]: ConditionValueSelectorComponent | null;
} = {
  [BillingRuleConditionTarget.TrailerType]: (props) => (
    <TrailerTypeSelect {...props} sx={{ maxWidth: 300, display: "flex" }} />
  ),
  [BillingRuleConditionTarget.Date]: DateInput,
  [BillingRuleConditionTarget.Otherwise]: null,
  [BillingRuleConditionTarget.Mileage]: MileageInput,
  [BillingRuleConditionTarget.Receiver]: (props) => (
    <BusinessEntityInput
      {...props}
      businessEntityType={BusinessEntityType.Receiver}
      sx={{ maxWidth: 300, display: "flex" }}
    />
  ),
  [BillingRuleConditionTarget.Shipper]: (props) => (
    <BusinessEntityInput
      {...props}
      businessEntityType={BusinessEntityType.Shipper}
      sx={{ maxWidth: 300, display: "flex" }}
    />
  ),
  [BillingRuleConditionTarget.Time]: TimeInput,
  [BillingRuleConditionTarget.Trailer]: (props) => (
    <TrailerInput {...props} sx={{ maxWidth: 300, display: "flex" }} />
  ),
  [BillingRuleConditionTarget.Tractor]: (props) => (
    <TractorInput {...props} sx={{ maxWidth: 300, display: "flex" }} />
  ),
  [BillingRuleConditionTarget.DetentionTime]: DurationInput,
  [BillingRuleConditionTarget.Goods]: (
    props: ConditionValueSelectorComponentProps
  ) => <GoodsInput {...props} sx={{ maxWidth: 300, display: "flex" }} />,
  [BillingRuleConditionTarget.Volume]: VolumeInput,
  [SupplierContractConditionTarget.PurchaseTime]: TimeInput,
  [SupplierContractConditionTarget.PurchaseQuantity]: NumberInput,
  [BillingRuleConditionTarget.FuelCost]: NumberInput,
  [BillingRuleConditionTarget.CustomerState]: ConditionStateSelect,
  [BillingRuleConditionTarget.ShipperState]: ConditionStateSelect,
  [BillingRuleConditionTarget.ReceiverState]: ConditionStateSelect,
  [BillingRuleConditionTarget.ShipperCity]: (props) => (
    <CityInput {...props} sx={{ maxWidth: 300, display: "flex" }} />
  ),
  [BillingRuleConditionTarget.ReceiverCity]: (props) => (
    <CityInput {...props} sx={{ maxWidth: 300, display: "flex" }} />
  ),
};

type BillingRuleConditionFormProps = {
  condition: BillingRuleCondition;
  onChange: (condition: BillingRuleCondition) => void;
  entity: "billingRule";
  customer?: GetCustomerDetailsQuery["customerById"];
};

type SupplierContractConditionFormProps = {
  condition: SupplierContractCondition;
  entity: "supplierContract";
  onChange: (condition: SupplierContractCondition) => void;
  customer?: undefined;
};

type ConditionFormProps =
  | BillingRuleConditionFormProps
  | SupplierContractConditionFormProps;

const targetOperatorConstraints: {
  [key: string]: BillingRuleConditionOperator[] | null;
} = {
  [BillingRuleConditionTarget.Shipper]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.Receiver]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.TrailerType]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.Trailer]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.Tractor]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.Goods]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.CustomerState]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.ShipperState]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.ReceiverState]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.ShipperCity]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
  [BillingRuleConditionTarget.ReceiverCity]: [
    BillingRuleConditionOperator.Equals,
    BillingRuleConditionOperator.NotEquals,
  ],
};

// These stay on the condition row. The rest drop to a row underneath.
const inlineConditionParameterTypes: BillingRuleConditionParameterType[] = [
  BillingRuleConditionParameterType.FuelCostArea,
  BillingRuleConditionParameterType.FuelCostPeriod,
];

const ConditionForm = ({
  condition,
  customer,
  onChange: propsOnChange,
  entity,
}: ConditionFormProps) => {
  const ValueSelectorComponent =
    ConditionValueSelectorComponentByTarget[condition.target];
  const { t } = useTranslation("billingRules");
  // Built from the target, so rules saved before a parameter existed show it.
  const conditionParameters: BillingRuleConditionParameter[] =
    "parameters" in condition
      ? buildConditionParameters(
          condition.target as BillingRuleConditionTarget,
          condition.parameters
        )
      : [];
  const visibleParameters = visibleConditionParameters(conditionParameters);
  const onChange: (
    condition: BillingRuleCondition | SupplierContractCondition
  ) => void = propsOnChange as (
    condition: BillingRuleCondition | SupplierContractCondition
  ) => void;
  const inlineParameters = visibleParameters.filter((parameter) =>
    inlineConditionParameterTypes.includes(parameter.type)
  );
  const settingsParameters = visibleParameters.filter(
    (parameter) => !inlineConditionParameterTypes.includes(parameter.type)
  );
  const renderParameter = (parameter: BillingRuleConditionParameter) => (
    <Box key={parameter.type} flex={1} display="flex" flexDirection="column">
      <ConditionParameterForm
        conditionParameter={parameter}
        onChange={(conditionParameter) =>
          onChange({
            ...(condition as BillingRuleCondition),
            parameters: conditionParameters.map((p) =>
              p.type === conditionParameter.type ? conditionParameter : p
            ),
          })
        }
      />
    </Box>
  );
  return (
    <Stack spacing={1} flex={1}>
      <Stack direction="row" alignItems="center" spacing={1} flex={1}>
      <Box flex={5}>
        {entity === "billingRule" ? (
          <Select data-testid={testIds.conditionFormBillingRuleTarget}
            label="Condition"
            size="small"
            value={condition.target}
            onChange={(event) => {
              const conditionTarget = event.target
                .value as BillingRuleConditionTarget;

              const conditionParameterTypes =
                conditionParametersPerTarget[conditionTarget] || [];
              onChange({
                ...condition,
                target: conditionTarget,
                value: null,
                operator: BillingRuleConditionOperator.Equals,
                parameters: conditionParameterTypes.map((type) => ({
                  type,
                  value:
                    condition.parameters?.find((p) => p.type === type)?.value ||
                    null,
                })),
              });
            }}
            sx={{ display: "flex" }}
          >
            {Object.values(BillingRuleConditionTarget).map((target) => (
              <MenuItem data-testid={`${testIds.conditionFormBillingRuleTargetOption}-${target}`} key={target} value={target}>
                <ListItemText
                  primary={billingRuleConditionTargetLabel(target)}
                />
              </MenuItem>
            ))}
          </Select>
        ) : entity === "supplierContract" ? (
          <Select data-testid={testIds.conditionFormSupplierContractTarget}
            label="Condition"
            size="small"
            value={condition.target}
            onChange={(event) => {
              onChange({
                ...condition,
                target: event.target.value as SupplierContractConditionTarget,
                value: null,
                operator: BillingRuleConditionOperator.Equals,
              });
            }}
            sx={{ display: "flex" }}
          >
            {Object.values(SupplierContractConditionTarget).map((target) => (
              <MenuItem data-testid={`${testIds.conditionFormSupplierContractTargetOption}-${target}`} key={target} value={target}>
                <ListItemText primary={enumLabel(target)} />
              </MenuItem>
            ))}
          </Select>
        ) : null}
      </Box>

      {inlineParameters.length ? (
        <Box flex={3} display="flex">
          <Stack direction="row" spacing={1} flex={1}>
            {inlineParameters.map(renderParameter)}
          </Stack>
        </Box>
      ) : null}
      <Box flex={2}>
        {condition.target &&
        condition.target !== BillingRuleConditionTarget.Otherwise ? (
          <Select data-testid={testIds.conditionFormOperator}
            label="Operator"
            size="small"
            value={condition.operator}
            onChange={(event) => {
              const operator = event.target
                .value as BillingRuleConditionOperator;
              onChange({
                ...condition,
                operator,
                value:
                  operator === BillingRuleConditionOperator.IsBetween
                    ? [condition.value, null]
                    : condition.value,
              });
            }}
            sx={{ display: "flex" }}
          >
            {(
              targetOperatorConstraints[condition.target] ||
              Object.values(BillingRuleConditionOperator)
            ).map((operator) => (
              <MenuItem data-testid={`${testIds.conditionFormOperatorOption}-${operator}`} key={operator} value={operator}>
                <ListItemText
                  primary={billingRuleConditionOperatorLabel(operator)}
                />
              </MenuItem>
            ))}
          </Select>
        ) : null}
      </Box>
      {condition.target &&
      condition.operator &&
      condition.operator !== BillingRuleConditionOperator.IsBetween &&
      ValueSelectorComponent ? (
        <Box flex={5} display="flex">
          <ValueSelectorComponent
            customer={customer}
            value={condition.value}
            onChange={(value) =>
              onChange({
                ...condition,
                value,
              })
            }
          />
        </Box>
      ) : null}
      {condition.target &&
      condition.operator === BillingRuleConditionOperator.IsBetween &&
      ValueSelectorComponent ? (
        <Box flex={5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ValueSelectorComponent
              customer={customer}
              value={condition.value[0]}
              onChange={(value) =>
                onChange({
                  ...condition,
                  value: [value, condition.value[1]],
                })
              }
            />

            <Typography variant="body2">{t("form.and")}</Typography>
            <ValueSelectorComponent
              customer={customer}
              value={condition.value[1]}
              onChange={(value) =>
                onChange({
                  ...condition,
                  value: [condition.value[0], value],
                })
              }
            />
          </Stack>
        </Box>
      ) : null}
      </Stack>
      {settingsParameters.length ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          {settingsParameters.map(renderParameter)}
          <Box flex={2} />
        </Stack>
      ) : null}
    </Stack>
  );
};

export default ConditionForm;
